import { AddressFallbackRecord, AddressRecord } from '@navio/nsdk';
import { BookingAddress } from 'noddi-types';
import { NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { ApiErrorMessage, ErrorCodes, NoddiInput, NoddiTooltip } from 'noddi-ui-web';
import { useState } from 'react';
import { addressRecordToFallback, getLongAddressName } from '../../utils/address';
import { OutsideServiceAreaInfo } from '../BookingTimeWindowPicker/OutsideServiceAreaInfo';
import { AddressRecoveryForm } from './AddressRecoveryForm';
import { AddressSearch } from './AddressSearch';
import { AddressSearchInline } from './AddressSearchInline';
import { AddressFallbackTranslations, AddressSearchTranslations } from './types';
import { useAddressResolution } from './useAddressResolution';

type Props = {
  label?: string;
  address?: { id: number; name: string } | AddressRecord | BookingAddress;
  /**
   * Form-friendly `{ id, name }` callback when a place resolves successfully. Coordinates come
   * along so callers can place the address without reading it back by id.
   */
  onAddressCreation: (address?: { id: number; name: string; latitude: number; longitude: number }) => void;
  onAddressResolutionChange?: (isResolving: boolean) => void;
  /** Manual fallback fields (invoice address, etc.). Also synced from a resolved place. */
  onFallbackChange?: (fallback: AddressFallbackRecord | null) => void;
  enableFallback?: boolean;
  /**
   * When false, places outside the delivery area do not call onAddressCreation
   * and show {@link OutsideServiceAreaInfo}. Default true (forms / invoice).
   */
  skipIsInDeliveryAreaCheck?: boolean;
  tooltip?: string;
  hideLabel?: boolean;
  translations?: AddressSearchTranslations;
  fallbackTranslations?: AddressFallbackTranslations;
  errorCodes?: ErrorCodes;
  startIcon?: NoddiIconName;
  /** Restrict Places autocomplete (optional). */
  countryCodes?: string[];
  /** Names of addresses the caller already shows. A match does not call Places. */
  savedAddressNames?: readonly string[];
  /** Passed to create-from-place for brand-scoped delivery checks (optional). */
  brandDomains?: string[];
  /**
   * Search as an in-flow input + list instead of the popover combobox. Required
   * inside a bottom sheet, where the phone keyboard covers an anchored popover.
   */
  inlineSearch?: boolean;
};

const defaultFallbackTranslations: AddressFallbackTranslations = {
  cantFindAddress: "Can't find your address? Enter it manually",
  backToSearch: 'Back to address search',
  address: 'Street name',
  addressPlaceholder: 'Street name',
  streetNumber: 'Number',
  streetNumberPlaceholder: 'Nr.',
  zipcode: 'Zipcode',
  zipcodePlaceholder: 'Zipcode',
  city: 'City',
  cityPlaceholder: 'City'
};

/**
 * Shared Places search + optional manual fallback for forms and invoice address.
 * Booking delivery policy (NoServiceOffered, brand map, tracking) lives in app-level
 * BookingAddressPicker — do not push those concerns here.
 */
export const AddressPicker = ({
  address,
  onAddressCreation,
  onAddressResolutionChange,
  onFallbackChange,
  enableFallback = false,
  skipIsInDeliveryAreaCheck = true,
  tooltip,
  label = 'Address',
  hideLabel = false,
  translations,
  fallbackTranslations,
  errorCodes,
  startIcon,
  countryCodes,
  brandDomains,
  savedAddressNames = [],
  inlineSearch = false
}: Props) => {
  const [showFallback, setShowFallback] = useState(false);
  const [streetName, setStreetName] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [fallbackZipcode, setFallbackZipcode] = useState('');
  const [fallbackCity, setFallbackCity] = useState('');
  const [isOutsideServiceArea, setIsOutsideServiceArea] = useState(false);

  const ft = fallbackTranslations ?? defaultFallbackTranslations;

  const buildFallback = (name: string, number: string, zipcode: string, city: string): AddressFallbackRecord => ({
    address: [name, number].filter(Boolean).join(' ').trim(),
    zipcode,
    city
  });

  const handleFallbackChange = (name: string, number: string, zipcode: string, city: string) => {
    onFallbackChange?.(buildFallback(name, number, zipcode, city));
  };

  const clearOutsideServiceAreaWarning = () => {
    setIsOutsideServiceArea(false);
  };

  const {
    resolve: createAddressFromPlaceId,
    recoveryFormProps,
    isPending: isAddressPending,
    clear: resetCreateAddressFromPlaceId,
    error: addressError
  } = useAddressResolution({
    onSuccess: (data) => {
      const { isInDeliveryArea, ...rest } = data;
      setIsOutsideServiceArea(!skipIsInDeliveryAreaCheck && !isInDeliveryArea);

      if (isInDeliveryArea || skipIsInDeliveryAreaCheck) {
        onAddressCreation({
          id: rest.id,
          name: getLongAddressName(rest),
          latitude: rest.latitude,
          longitude: rest.longitude
        });
        // Keep fallback consumers (e.g. invoice address) in sync with the resolved place.
        onFallbackChange?.(addressRecordToFallback(rest));
        return;
      }

      onAddressCreation(undefined);
      onFallbackChange?.(null);
    },
    onError: () => {
      clearOutsideServiceAreaWarning();
      onAddressCreation(undefined);
      onFallbackChange?.(null);
    },
    onResolutionChange: onAddressResolutionChange
  });

  const handleSuggestionSelect = (addressSuggestion: { placeId: string }) => {
    onAddressCreation(undefined);
    onFallbackChange?.(null);
    createAddressFromPlaceId({
      placeId: addressSuggestion.placeId,
      ...(brandDomains?.length ? { brandDomains } : {})
    });
  };

  const handleSearchClear = () => {
    clearOutsideServiceAreaWarning();
    resetCreateAddressFromPlaceId();
    onAddressCreation(undefined);
    onFallbackChange?.(null);
  };

  const handleSwitchToFallback = () => {
    setShowFallback(true);
    clearOutsideServiceAreaWarning();
    onAddressCreation(undefined);
    onFallbackChange?.(null);
    resetCreateAddressFromPlaceId();
  };

  const handleBackToSearch = () => {
    setShowFallback(false);
    setStreetName('');
    setStreetNumber('');
    setFallbackZipcode('');
    setFallbackCity('');
    onFallbackChange?.(null);
  };

  return (
    <div className='flex flex-col gap-0.5'>
      {label && !hideLabel && (
        <div className='flex items-center gap-2'>
          <label className='text-sm'>{label}</label>
          {tooltip && <NoddiTooltip content={tooltip} />}
        </div>
      )}

      {showFallback ? (
        <div className='flex flex-col gap-4'>
          <div className='flex gap-4'>
            <div className='flex-1'>
              <NoddiInput
                startAdornment={<NoddiIcon name='MapPin' className='ml-1 text-muted-foreground' />}
                label={ft.address}
                placeholder={ft.addressPlaceholder}
                value={streetName}
                maxLength={1024}
                onChange={(e) => {
                  setStreetName(e.target.value);
                  handleFallbackChange(e.target.value, streetNumber, fallbackZipcode, fallbackCity);
                }}
              />
            </div>
            <div className='w-24'>
              <NoddiInput
                label={ft.streetNumber}
                placeholder={ft.streetNumberPlaceholder}
                value={streetNumber}
                maxLength={10}
                onChange={(e) => {
                  setStreetNumber(e.target.value);
                  handleFallbackChange(streetName, e.target.value, fallbackZipcode, fallbackCity);
                }}
              />
            </div>
          </div>
          <div className='flex gap-4'>
            <NoddiInput
              label={ft.zipcode}
              placeholder={ft.zipcodePlaceholder}
              value={fallbackZipcode}
              maxLength={12}
              onChange={(e) => {
                setFallbackZipcode(e.target.value);
                handleFallbackChange(streetName, streetNumber, e.target.value, fallbackCity);
              }}
            />
            <NoddiInput
              label={ft.city}
              placeholder={ft.cityPlaceholder}
              value={fallbackCity}
              maxLength={1024}
              onChange={(e) => {
                setFallbackCity(e.target.value);
                handleFallbackChange(streetName, streetNumber, fallbackZipcode, e.target.value);
              }}
            />
          </div>
          <button
            type='button'
            onClick={handleBackToSearch}
            className='w-fit py-2 text-left text-sm text-primary underline underline-offset-2'
          >
            {ft.backToSearch}
          </button>
        </div>
      ) : (
        <>
          {inlineSearch ? (
            <AddressSearchInline
              translations={translations}
              loading={isAddressPending}
              startIcon={startIcon}
              countryCodes={countryCodes}
              savedAddressNames={savedAddressNames}
              onSelect={handleSuggestionSelect}
              onClear={handleSearchClear}
              defaultValue={address}
            />
          ) : (
            <AddressSearch
              translations={translations}
              loading={isAddressPending}
              startIcon={startIcon}
              countryCodes={countryCodes}
              savedAddressNames={savedAddressNames}
              onSelect={handleSuggestionSelect}
              onClear={handleSearchClear}
              defaultValue={address}
              fullWidthPopover={false}
            />
          )}
          {recoveryFormProps ? (
            <AddressRecoveryForm {...recoveryFormProps} translations={translations?.recovery} />
          ) : (
            addressError && <ApiErrorMessage error={addressError} errorCodes={errorCodes} />
          )}
          {isOutsideServiceArea && <OutsideServiceAreaInfo />}
          {enableFallback && (
            <button
              type='button'
              onClick={handleSwitchToFallback}
              className='mt-2 w-fit py-2 text-left text-sm text-primary underline underline-offset-2'
            >
              {ft.cantFindAddress}
            </button>
          )}
        </>
      )}
    </div>
  );
};
